// src/components/ProductSpotlight.js
//
// A rotating product banner for the parent and educator dashboards, sitting
// in the footer row to the right of the Logout button.
//
// Source of truth is the same one the Products page uses: `listProducts()`
// against the Firestore `products` collection. Not the static
// PRODUCT_CATALOG — that file is the plan/subscription list for the signup
// flow, and it would drift from the real catalog the moment anyone added a
// product through the admin panel.
//
// Two kinds of rotation, because they answer different problems:
//
//   1. The starting product is picked at random on mount. Every refresh or
//      revisit opens on a different one, so someone who ignores the banner
//      today still meets something new tomorrow. Without this the same card
//      sits there forever and becomes furniture.
//
//   2. It also advances on a timer while the person is on the page, and the
//      arrows and dots move it by hand.
//
// The timer pauses on hover, on focus, and whenever the tab is hidden — a
// banner that moves under the cursor as you reach for it is worse than one
// that never moves at all.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listProducts } from "../admin/productCatalog/productsService";
import "./ProductSpotlight.css";

const ROTATE_MS = 7000;

// Mirrors FirestoreProductCard. A product with no variant has no price and
// no image and renders as an empty box, so it isn't worth a slot.
const firstVariant = (product) =>
  product?.variants?.length ? product.variants[0] : null;

const priceOf = (product) => {
  const variant = firstVariant(product);
  if (!variant) return null;
  const value = product.hasSizes ? variant.sizes?.[0]?.price : variant.price;
  return typeof value === "number" && value > 0 ? value : null;
};

const imageOf = (product) =>
  firstVariant(product)?.image || product?.image || null;

// "3 colors · 5 sizes" — a small signal that there's more to see on the
// product page than the single photo shown here.
const optionSummary = (product) => {
  const parts = [];
  const colors = product.variants?.length || 0;
  const sizes = firstVariant(product)?.sizes?.length || 0;

  if (product.hasColors && colors > 1) {
    parts.push(`${colors} colors`);
  }
  if (product.hasSizes && sizes > 0) {
    parts.push(`${sizes} ${sizes === 1 ? "size" : "sizes"}`);
  }

  return parts.join(" · ");
};

const ProductSpotlight = ({
  heading = "From the shop",
  subheading = "A different pick each time you visit.",
}) => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    listProducts()
      .then((all) => {
        if (cancelled) return;

        const shelf = all
          .filter((product) => product.active !== false)
          .filter((product) => firstVariant(product));

        setProducts(shelf);

        // Random opening card. Set here rather than in useState so it
        // re-rolls whenever the catalog itself loads.
        setIndex(shelf.length ? Math.floor(Math.random() * shelf.length) : 0);
      })
      .catch((error) => {
        // The dashboard is not the shop. If the catalog can't be read the
        // banner just doesn't appear — it should never block the page or
        // throw a visible error at someone trying to reach their subjects.
        console.warn("[ProductSpotlight] could not load products:", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const advance = useCallback(
    (step) => {
      setIndex((current) => {
        if (!products.length) return 0;
        return (current + step + products.length) % products.length;
      });
    },
    [products.length]
  );

  useEffect(() => {
    if (paused || products.length < 2) return undefined;

    // A hidden tab still runs timers, so without this the banner races
    // through the whole catalog while nobody is looking.
    const onVisibility = () => {
      clearInterval(timerRef.current);
      if (!document.hidden) {
        timerRef.current = setInterval(() => advance(1), ROTATE_MS);
      }
    };

    timerRef.current = setInterval(() => advance(1), ROTATE_MS);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [advance, paused, products.length]);

  if (!products.length) return null;

  const product = products[index];
  const price = priceOf(product);
  const image = imageOf(product);
  const options = optionSummary(product);

  return (
    <section
      className="sbl-spotlight"
      aria-roledescription="carousel"
      aria-label={heading}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={(event) => {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          advance(1);
        }
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          advance(-1);
        }
      }}
    >
      <header className="sbl-spotlight-head">
        <div>
          <h2>{heading}</h2>
          {subheading && <p>{subheading}</p>}
        </div>

        {products.length > 1 && (
          <div className="sbl-spotlight-arrows">
            <button
              type="button"
              onClick={() => advance(-1)}
              aria-label="Previous product"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => advance(1)}
              aria-label="Next product"
            >
              ›
            </button>
          </div>
        )}
      </header>

      <div
        className="sbl-spotlight-card"
        key={product.id}
        aria-roledescription="slide"
        aria-label={`${index + 1} of ${products.length}`}
      >
        <div className="sbl-spotlight-art">
          {image ? (
            <img
              src={image}
              alt=""
              loading="lazy"
              onError={(event) => {
                // A broken-image icon on something you're asking someone to
                // buy is worse than no image, so failures fall back to the
                // gradient panel behind it.
                event.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </div>

        <div className="sbl-spotlight-body">
          <h3>{product.name}</h3>

          {product.description && (
            <p className="sbl-spotlight-blurb">{product.description}</p>
          )}

          <div className="sbl-spotlight-meta">
            {price !== null && (
              <span className="sbl-price">${price.toFixed(2)}</span>
            )}
            {options && <span className="sbl-options">{options}</span>}
          </div>

          <button
            type="button"
            className="sbl-spotlight-primary"
            onClick={() => navigate("/products")}
          >
            Shop now
          </button>
        </div>
      </div>

      {products.length > 1 && (
        <div className="sbl-spotlight-dots">
          {products.map((entry, dotIndex) => (
            <button
              key={entry.id}
              type="button"
              aria-label={entry.name}
              aria-current={dotIndex === index}
              className={dotIndex === index ? "is-active" : ""}
              onClick={() => setIndex(dotIndex)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default ProductSpotlight;
